
import React, { useState, useEffect } from 'react';
import { X, RotateCw, Sigma, LayoutGrid, ArrowRightLeft, ListOrdered, Save, ArrowLeft, ArrowRight, Info, CheckCircle2, TrendingUp, Hash } from 'lucide-react';

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

const DataRestructureModal = ({ isOpen, onClose, tables, onSaveDataset }) => {
    const [step, setStep] = useState(1); // 1: Config, 2: Preview
    const [mode, setMode] = useState('summarize'); // summarize, pivot, transpose, running_total, record_id
    const [sourceTable, setSourceTable] = useState('');
    const [columns, setColumns] = useState([]);

    // Config States
    const [config, setConfig] = useState({
        groupBy: [],
        aggregations: [{ column: '', func: 'SUM', label: 'Total' }],
        rowField: '',
        colField: '',
        valField: '',
        aggFunc: 'SUM',
        keyColumns: [],
        dataColumns: [],
        orderColumn: '',
        valueColumn: '',
        partitionColumn: ''
    });

    const [previewData, setPreviewData] = useState([]);
    const [generatedSql, setGeneratedSql] = useState('');
    const [newDatasetName, setNewDatasetName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSourceTable('');
            setPreviewData([]);
            setGeneratedSql('');
            setError('');
            setNewDatasetName('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (sourceTable) {
            const table = tables.find(t => t.name === sourceTable);
            const cols = table ? table.columns : [];
            setColumns(cols);

            // Full reset to prevent stale columns
            setConfig({
                groupBy: [],
                aggregations: [{ column: cols[0] || '', func: 'SUM', label: 'Total' }],
                rowField: cols[0] || '',
                colField: cols[1] || '',
                valField: cols[2] || '',
                aggFunc: 'SUM',
                keyColumns: [], // Default to empty for full transpose
                dataColumns: cols, // Default to all columns
                orderColumn: cols[0] || '',
                valueColumn: cols[1] || '',
                partitionColumn: '',
                attributeName: 'Metric',
                valueName: 'Value'
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceTable]);

    // Force Full Transpose Config when mode changes to transpose
    useEffect(() => {
        if (mode === 'transpose' && columns.length > 0) {
            setConfig(prev => ({
                ...prev,
                keyColumns: [],
                dataColumns: columns
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const getCompatibleTables = () => {
        if (mode === 'pivot' || mode === 'transpose') {
            return tables.filter(t => t.columns && t.columns.length >= 2);
        }
        return tables;
    };

    const handlePreview = async () => {
        if (!sourceTable) {
            setError('Please select a source table.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/data/preview-restructure`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ sourceTable, type: mode, config })
            });

            const data = await res.json();
            if (res.ok) {
                setPreviewData(data.preview);
                setGeneratedSql(data.sql);
                setStep(2);
            } else {
                setError(data.error || 'Failed to restructure data');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!newDatasetName.trim()) {
            setError('Please enter a name for the new dataset.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/data/create-view`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    viewName: newDatasetName,
                    sql: generatedSql
                })
            });

            const data = await res.json();
            if (res.ok) {
                onSaveDataset && onSaveDataset();
                onClose();
            } else {
                setError(data.error || 'Failed to create dataset');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleMultiSelect = (field, value) => {
        setConfig(prev => {
            const current = prev[field] || [];
            if (current.includes(value)) {
                return { ...prev, [field]: current.filter(v => v !== value) };
            } else {
                return { ...prev, [field]: [...current, value] };
            }
        });
    };

    if (!isOpen) return null;

    return (
        <div className="style-modal-overlay">
            <div className="style-modal-content" style={{ maxWidth: '1000px', width: '95%', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
                <div className="style-modal-header" style={{ padding: '20px', borderBottom: '1px solid var(--glass-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="icon-container-premium" style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}>
                            <RotateCw size={20} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Data Restructuring</h2>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dim)' }}>Transpose, Pivot, and Summarize datasets</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', gap: '24px' }}>

                    {error && (
                        <div style={{ width: '100%', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(239, 68, 68, 0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Info size={16} />
                            {error}
                        </div>
                    )}

                    {step === 1 ? (
                        <>
                            {/* Left Side: Tool Selection */}
                            <div style={{ width: '220px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '8px' }}>Operation Type</label>
                                {[
                                    { id: 'summarize', name: 'Summarize', icon: Sigma, desc: 'Group & Aggregate' },
                                    { id: 'pivot', name: 'Pivot', icon: LayoutGrid, desc: 'Create Cross-tab' },
                                    { id: 'transpose', name: 'Transpose', icon: ArrowRightLeft, desc: 'Rows ↔ Columns' },
                                    { id: 'running_total', name: 'Running Total', icon: TrendingUp, desc: 'Cumulative Sum' },
                                    { id: 'record_id', name: 'Record ID', icon: Hash, desc: 'Generate unique IDs' }
                                ].map(tool => (
                                    <div
                                        key={tool.id}
                                        onClick={() => setMode(tool.id)}
                                        style={{
                                            padding: '12px',
                                            borderRadius: '12px',
                                            background: mode === tool.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                                            border: '1px solid',
                                            borderColor: mode === tool.id ? 'var(--accent-primary)' : 'var(--glass-border)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}
                                    >
                                        <tool.icon size={18} style={{ color: mode === tool.id ? 'var(--accent-primary)' : 'var(--text-dim)' }} />
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: mode === tool.id ? 'white' : 'var(--text-main)' }}>{tool.name}</div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>{tool.desc}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Center: Config */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div className="form-group">
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '8px', display: 'block' }}>Source Dataset</label>
                                    <select
                                        className="premium-input"
                                        value={sourceTable}
                                        onChange={(e) => setSourceTable(e.target.value)}
                                    >
                                        <option value="">Select Table...</option>
                                        {getCompatibleTables().map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                                    </select>
                                </div>

                                <div style={{ background: 'var(--bg-tertiary)', borderRadius: '16px', padding: '20px', border: '1px solid var(--glass-border)', flex: 1 }}>
                                    <h3 style={{ margin: 0, fontSize: '1rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        Configure {mode.replace('_', ' ').toUpperCase()}
                                    </h3>

                                    {mode === 'summarize' && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Group By Fields</label>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                    {columns.map(c => (
                                                        <button
                                                            key={c}
                                                            onClick={() => toggleMultiSelect('groupBy', c)}
                                                            style={{
                                                                padding: '6px 12px', borderRadius: '20px', border: '1px solid var(--glass-border)', fontSize: '0.75rem', cursor: 'pointer',
                                                                background: config.groupBy.includes(c) ? 'var(--accent-primary)' : 'transparent',
                                                                color: config.groupBy.includes(c) ? 'white' : 'var(--text-dim)'
                                                            }}
                                                        >
                                                            {c}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Aggregations</label>
                                                {config.aggregations.map((agg, idx) => (
                                                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px' }}>
                                                        <select className="premium-input-small" value={agg.column} onChange={e => {
                                                            const newAggs = [...config.aggregations];
                                                            newAggs[idx].column = e.target.value;
                                                            setConfig({ ...config, aggregations: newAggs });
                                                        }}>
                                                            <option value="">Column...</option>
                                                            {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                        <select className="premium-input-small" value={agg.func} onChange={e => {
                                                            const newAggs = [...config.aggregations];
                                                            newAggs[idx].func = e.target.value;
                                                            setConfig({ ...config, aggregations: newAggs });
                                                        }}>
                                                            <option value="SUM">Sum</option>
                                                            <option value="AVG">Average</option>
                                                            <option value="COUNT">Count</option>
                                                            <option value="MAX">Max</option>
                                                            <option value="MIN">Min</option>
                                                        </select>
                                                        <input className="premium-input-small" placeholder="Label" value={agg.label} onChange={e => {
                                                            const newAggs = [...config.aggregations];
                                                            newAggs[idx].label = e.target.value;
                                                            setConfig({ ...config, aggregations: newAggs });
                                                        }} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {mode === 'pivot' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Row Field (e.g. Regions)</label>
                                                <select className="premium-input-small" value={config.rowField} onChange={e => setConfig({ ...config, rowField: e.target.value })}>
                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Column Field (e.g. Months)</label>
                                                <select className="premium-input-small" value={config.colField} onChange={e => setConfig({ ...config, colField: e.target.value })}>
                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Value Field (e.g. Sales)</label>
                                                <select className="premium-input-small" value={config.valField} onChange={e => setConfig({ ...config, valField: e.target.value })}>
                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Aggregation</label>
                                                <select className="premium-input-small" value={config.aggFunc} onChange={e => setConfig({ ...config, aggFunc: e.target.value })}>
                                                    <option value="SUM">Sum</option>
                                                    <option value="COUNT">Count</option>
                                                    <option value="AVG">Average</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {mode === 'transpose' && (
                                        <div style={{ padding: '30px', textAlign: 'center', background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                                            <ArrowRightLeft size={48} style={{ color: 'var(--accent-primary)', marginBottom: '16px', opacity: 0.8 }} />
                                            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>Smart Transpose Ready</h3>
                                            <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                                                We will automatically convert <strong>all {columns.length} columns</strong> into rows.
                                                <br />A "Record ID" will be generated to keep track of the original rows.
                                            </p>
                                        </div>
                                    )}

                                    {mode === 'running_total' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Order By Column (usually Date or ID)</label>
                                                <select className="premium-input-small" value={config.orderColumn} onChange={e => setConfig({ ...config, orderColumn: e.target.value })}>
                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Column to Total</label>
                                                <select className="premium-input-small" value={config.valueColumn} onChange={e => setConfig({ ...config, valueColumn: e.target.value })}>
                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: '8px' }}>Group/Partition By (Optional)</label>
                                                <select className="premium-input-small" value={config.partitionColumn} onChange={e => setConfig({ ...config, partitionColumn: e.target.value })}>
                                                    <option value="">None</option>
                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {mode === 'record_id' && (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <ListOrdered size={48} style={{ color: 'var(--accent-primary)', marginBottom: '16px', opacity: 0.5 }} />
                                            <p style={{ color: 'var(--text-dim)' }}>This tool will add a new column <strong>"record_id"</strong> with a unique sequential number for every row in the dataset.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div className="form-group" style={{ flex: 1, marginRight: '20px' }}>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '8px', display: 'block' }}>Save as New Dataset</label>
                                    <input
                                        type="text"
                                        className="premium-input"
                                        placeholder="e.g. sales_summarized_by_region"
                                        value={newDatasetName}
                                        onChange={e => setNewDatasetName(e.target.value)}
                                    />
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Source: <strong>{sourceTable}</strong></div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Operation: <strong>{mode.toUpperCase()}</strong></div>
                                </div>
                            </div>

                            <div style={{ border: '1px solid var(--glass-border)', borderRadius: '16px', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-tertiary)' }}>
                                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '16px 20px', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <CheckCircle2 size={18} style={{ color: '#10b981' }} />
                                        <span style={{ fontSize: '1rem', fontWeight: 600 }}>Preview Restructured Data</span>
                                    </div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Showing first 10 rows</span>
                                </div>

                                <div style={{ overflowX: 'auto', flex: 1 }}>
                                    {previewData.length > 0 ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr style={{ background: 'rgba(255,255,255,0.02)', textAlign: 'left' }}>
                                                    {Object.keys(previewData[0]).map(key => (
                                                        <th key={key} style={{ padding: '14px 20px', borderBottom: '1px solid var(--glass-border)', color: 'var(--accent-primary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{key}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewData.map((row, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)', transition: 'background 0.2s' }}>
                                                        {Object.values(row).map((val, j) => (
                                                            <td key={j} style={{ padding: '12px 20px', whiteSpace: 'nowrap', color: 'var(--text-main)' }}>{val === null ? <em style={{ opacity: 0.3 }}>null</em> : String(val)}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                            <Info size={32} opacity={0.3} />
                                            <p>No results found for this configuration.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="style-modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    {step === 2 && (
                        <button className="btn-secondary-premium" onClick={() => setStep(1)} disabled={loading}>
                            <ArrowLeft size={16} style={{ marginRight: '8px' }} /> Back to Config
                        </button>
                    )}

                    <button
                        className="btn-action-premium"
                        onClick={step === 1 ? handlePreview : handleSave}
                        disabled={loading || !sourceTable}
                        style={{ background: step === 2 ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : '' }}
                    >
                        {loading ? 'Processing...' : (
                            step === 1 ? (
                                <><ArrowRight size={16} style={{ marginRight: '8px' }} /> Preview Results</>
                            ) : (
                                <><Save size={16} style={{ marginRight: '8px' }} /> Create Dataset</>
                            )
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                .premium-input-small {
                    width: 100%;
                    padding: 8px 10px;
                    background: var(--bg-primary);
                    border: 1px solid var(--glass-border);
                    border-radius: 6px;
                    color: var(--text-primary);
                    font-size: 0.8rem;
                    outline: none;
                }
            `}</style>
        </div>
    );
};

export default DataRestructureModal;
